import * as React from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BarChart3, ExternalLink, FileText, Folder, Globe, Lock, LockOpen, Search, StickyNote, X, ChartPie, Table2 } from 'lucide-react';
import type { ItemType } from '@shared/types';
import { pickLocale } from '@shared/types';
import { api, ApiError, qk } from '@client/lib/api';
import { useRoomHome, type RoomItem, type SearchResult } from '@client/lib/investor-api';
import { currentLocale } from '@client/lib/i18n';
import { fmtDate, fmtRelative } from '@client/lib/format';
import { track } from '@client/lib/tracker';
import { Badge } from '@client/components/ui/badge';
import { Button } from '@client/components/ui/button';
import { Input } from '@client/components/ui/input';
import { ErrorState, Skeleton } from '@client/components/ui/feedback';
import { MarkWhite } from '@client/components/brand/logo';
import { NdaGate } from '@client/components/investor/nda-gate';
import { UpdateCard } from '@client/components/investor/update-card';
import { cn } from '@client/lib/utils';

export const Route = createFileRoute('/_investor/rooms/$slug')({ component: RoomPage });

const TYPE_ICON: Record<ItemType, React.ComponentType<{ className?: string }>> = { folder: Folder, document: FileText, dashboard: BarChart3, page: StickyNote, embed: Globe, link: ExternalLink, captable: ChartPie, sheet: Table2 };

function RoomPage() {
  const { slug } = Route.useParams();
  const { t } = useTranslation();
  const locale = currentLocale();
  const qc = useQueryClient();
  const home = useRoomHome(slug);
  const [q, setQ] = React.useState('');
  const [results, setResults] = React.useState<SearchResult[] | null>(null);
  const entered = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (entered.current === slug) return;
    entered.current = slug;
    api(`/api/rooms/${slug}/enter`, { method: 'POST' }).then(() => qc.invalidateQueries({ queryKey: qk.rooms })).catch(() => undefined);
  }, [slug, qc]);

  React.useEffect(() => {
    if (!home.data?.room.id) return;
    const roomId = home.data.room.id;
    return () => track('room.left', { room_id: roomId });
  }, [home.data?.room.id]);

  React.useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => {
      api<{ results: SearchResult[] }>(`/api/rooms/${slug}/search?q=${encodeURIComponent(q.trim())}`, { signal: ctrl.signal }).then((r) => setResults(r.results)).catch(() => undefined);
    }, 300);
    return () => { window.clearTimeout(timer); ctrl.abort(); };
  }, [q, slug]);

  const acceptNda = useMutation({ mutationFn: () => api(`/api/rooms/${slug}/nda/accept`, { method: 'POST' }), onSuccess: () => { qc.invalidateQueries({ queryKey: qk.room(slug) }); qc.invalidateQueries({ queryKey: qk.rooms }); } });
  const ackNotice = useMutation({ mutationFn: () => api(`/api/rooms/${slug}/notice`, { method: 'POST' }), onSuccess: () => qc.invalidateQueries({ queryKey: qk.room(slug) }) });

  if (home.isPending) return <main className="mx-auto max-w-[1180px] px-5 py-8"><Skeleton className="h-40" /><Skeleton className="mt-6 h-64" /></main>;
  if (home.isError) {
    const err = home.error as ApiError;
    const reason = err instanceof ApiError ? err.reason : undefined;
    return <main className="mx-auto max-w-[640px] px-5 py-16"><ErrorState title={reason === 'grant_expired' ? t('room.expiredTitle') : t('common.notFound')} body={reason === 'grant_expired' ? t('room.expiredBody', { contact: 'ResilientX' }) : reason ? t(`errors.${reason}`, { defaultValue: t('errors.denied') }) : t('errors.denied')} onRetry={() => home.refetch()} retryLabel={t('common.retry')} /></main>;
  }
  const data = home.data;
  if (data.closed) {
    return (
      <main className="mx-auto max-w-[640px] px-5 py-16">
        <div className="rounded-card border border-border bg-card p-8 text-center">
          <Lock className="mx-auto mb-3 size-6 text-muted" />
          <h1 className="page-title">{t('room.closedTitle')}</h1>
          <p className="mt-2 text-body">{t('room.closedBody', { contact: data.contact.name })}</p>
          <Link to="/rooms" className="mt-5 inline-block text-[13px]">{t('common.back')}</Link>
        </div>
      </main>
    );
  }
  if (data.nda_pending) {
    return <NdaGate roomName={data.room.name} text={data.nda?.text ?? null} version={data.nda?.version ?? 1} onAccept={() => acceptNda.mutate()} busy={acceptNda.isPending} />;
  }
  const items = data.items ?? [];
  const roots = items.filter((i) => !i.parent_id);
  const children = (id: string) => items.filter((i) => i.parent_id === id);
  const access = data.access!;
  const description = pickLocale(data.room.description, locale);

  return (
    <main>
      <section className={cn('relative min-h-[180px] overflow-hidden', !data.room.cover_url && 'hero-navy')}>
        {data.room.cover_url ? <><img src={data.room.cover_url} alt="" className="absolute inset-0 size-full object-cover" /><div className="absolute inset-0 bg-navy/70" /></> : null}
        <div className="relative mx-auto flex max-w-[1180px] flex-col justify-end px-5 py-8 md:px-8">
          {!data.room.cover_url ? <MarkWhite size={32} className="mb-4 opacity-90" /> : null}
          <h1 className="text-[22px] font-[620] leading-7 tracking-[-0.02em] text-white md:text-[26px] md:leading-8">{data.room.name}</h1>
          {description ? <p className="mt-2 max-w-2xl text-[15px] leading-6 text-navy-muted">{description}</p> : null}
        </div>
      </section>
      <div className="mx-auto max-w-[1180px] px-5 py-6 md:px-8 md:py-8">
        {!access.notice_acknowledged ? (
          <div className="mb-6 flex items-start justify-between gap-4 rounded-card border border-border bg-card p-4 shadow-card animate-reveal" role="note">
            <p className="text-[13px] leading-5 text-body">{t('room.notice')}</p>
            <Button variant="ghost" size="sm" onClick={() => ackNotice.mutate()}>{t('common.gotIt')}</Button>
          </div>
        ) : null}
        <div className="grid gap-8 lg:grid-cols-[1fr_300px]">
          <div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('room.searchPlaceholder')} className="pl-9 pr-9" aria-label={t('common.search')} />
              {q ? <button className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted hover:text-foreground" onClick={() => setQ('')} aria-label={t('common.close')}><X className="size-4" /></button> : null}
            </div>
            {results ? (
              <section className="mt-5">
                <h2 className="eyebrow mb-2">{t('room.results')}</h2>
                {results.length === 0 ? <p className="text-[13px] text-muted">{t('room.noResults')}</p> : (
                  <ul className="divide-y divide-border rounded-card border border-border bg-card">
                    {results.map((r, i) => {
                      const Icon = TYPE_ICON[r.type];
                      return (
                        <li key={`${r.item_id}-${r.page_no ?? 't'}-${i}`}>
                          <Link to="/items/$id" params={{ id: r.item_id }} search={r.page_no ? { page: r.page_no } : undefined} className="flex items-start gap-3 px-4 py-3 no-underline hover:bg-row-hover">
                            <Icon className="mt-0.5 size-4 shrink-0 text-muted" />
                            <div className="min-w-0">
                              <p className="text-[14px] font-[520] text-foreground">{pickLocale(r.title, locale)}{r.page_no ? <span className="ml-2 text-[12.5px] font-normal text-muted">{t('common.page')} {r.page_no}</span> : null}</p>
                              {r.snippet ? <p className="mt-0.5 line-clamp-2 text-[12.5px] text-muted">{r.snippet}</p> : null}
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            ) : (
              <>
                {data.updates && data.updates.length > 0 ? (
                  <section className="mt-6">
                    <div className="mb-3 flex items-center justify-between">
                      <h2 className="eyebrow">{t('updates.title')}{data.unread_updates ? <Badge tone="good" className="ml-2">{t('updates.unread', { count: data.unread_updates })}</Badge> : null}</h2>
                      <Link to="/updates" search={{ room: slug }} className="text-[13px]">{t('updates.all')}</Link>
                    </div>
                    <div className="grid gap-3">{data.updates.slice(0, 3).map((u, idx) => <UpdateCard key={u.id} u={u} compact delay={idx * 40} />)}</div>
                  </section>
                ) : null}
                {data.whats_new && data.whats_new.length > 0 ? (
                  <section className="mt-6">
                    <h2 className="eyebrow mb-3">{t('room.whatsNew')}</h2>
                    <div className="grid gap-4 sm:grid-cols-3">
                      {data.whats_new.map((i, idx) => <ItemCard key={i.id} item={i} delay={idx * 40} />)}
                    </div>
                  </section>
                ) : null}
                <section className="mt-8">
                  <h2 className="eyebrow mb-3">{t('room.contents')}</h2>
                  {roots.length === 0 ? <p className="text-[13px] text-muted">{t('common.items', { count: 0 })}</p> : null}
                  <div className="space-y-6">
                    {roots.filter((i) => i.type !== 'folder').length > 0 ? <ItemList items={roots.filter((i) => i.type !== 'folder')} /> : null}
                    {roots.filter((i) => i.type === 'folder').map((f) => (
                      <div key={f.id}>
                        <h3 className="section-title mb-2 flex items-center gap-2"><Folder className="size-4 text-muted" />{pickLocale(f.title, locale)}</h3>
                        <ItemList items={children(f.id)} nested={children} />
                      </div>
                    ))}
                  </div>
                </section>
              </>
            )}
          </div>
          <aside className="space-y-4 lg:pt-1">
            <div className="rounded-card border border-border bg-card p-4">
              <h2 className="eyebrow mb-2">{t('room.yourAccess')}</h2>
              <p className="text-[14px] text-foreground">{access.expires_at ? t('room.validUntil', { date: fmtDate(access.expires_at, locale) }) : t('room.noExpiry')}</p>
              <p className="mt-1 flex items-center gap-1.5 text-[13px] text-muted">{access.controls.download ? <LockOpen className="size-3.5" /> : <Lock className="size-3.5" />}{access.controls.download ? t('room.downloadsAllowed') : t('room.downloadsNotAllowed')}</p>
              {access.firm_name ? <p className="mt-1 text-[13px] text-muted">{access.firm_name}</p> : null}
            </div>
            <div className="rounded-card border border-border bg-card p-4">
              <h2 className="eyebrow mb-2">{t('room.contact')}</h2>
              <p className="text-[14px] text-foreground">{data.contact.name}</p>
              <a href={`mailto:${data.contact.email}`} className="text-[13px]">{data.contact.email}</a>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

function ItemCard({ item, delay }: { item: RoomItem; delay: number }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const Icon = TYPE_ICON[item.type];
  return (
    <Link to="/items/$id" params={{ id: item.id }} className="block rounded-card border border-border bg-card p-4 no-underline shadow-card transition-[box-shadow,transform] duration-150 hover:-translate-y-px hover:shadow-card-hover animate-reveal" style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-center justify-between">
        <Icon className="size-4 text-muted" />
        {item.is_new ? <Badge tone="brand">{t('common.new')}</Badge> : null}
      </div>
      <p className="mt-3 text-[14px] font-[570] text-foreground">{pickLocale(item.title, locale)}</p>
      <p className="mt-1 text-[12.5px] text-muted">{t(`types.${item.type}`)}{item.page_count ? ` · ${t('common.pages', { count: item.page_count })}` : ''} · {fmtRelative(item.published_at, locale)}</p>
    </Link>
  );
}

function ItemList({ items, nested }: { items: RoomItem[]; nested?: (id: string) => RoomItem[] }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  return (
    <ul className="divide-y divide-border rounded-card border border-border bg-card">
      {items.map((i) => {
        const Icon = TYPE_ICON[i.type];
        const sub = i.type === 'folder' && nested ? nested(i.id) : [];
        return (
          <li key={i.id}>
            <Link to="/items/$id" params={{ id: i.id }} className="flex items-center gap-3 px-4 py-3 no-underline hover:bg-row-hover">
              <Icon className="size-4 shrink-0 text-muted" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-[520] text-foreground">{pickLocale(i.title, locale)}</p>
                <p className="text-[12.5px] text-muted">{t(`types.${i.type}`)}{i.page_count ? ` · ${t('common.pages', { count: i.page_count })}` : ''}{i.as_of ? ` · ${t('common.asOf', { date: fmtDate(i.as_of, locale) })}` : ''}{i.published_at ? ` · ${fmtRelative(i.published_at, locale)}` : ''}</p>
              </div>
              {i.is_new ? <Badge tone="brand">{t('common.new')}</Badge> : null}
            </Link>
            {sub.length > 0 ? <div className="pl-6"><ItemList items={sub} nested={nested} /></div> : null}
          </li>
        );
      })}
    </ul>
  );
}
